/**
 * Minga entity refs in prose (decision 0029, wave K §4.2): items (`DIC-12` with the project key, and the old `T-n`,
 * `I-n`, `C-n`), goals `G-n`, KPIs `K-n`, proposals `P-n`, runs `R-n`, the temporary teammates `W-n` / `V-n`,
 * outputs `O-n` (wave K), memory `M-n` (wave C (decision 0071)) and decisions `D-n` (decision 0144: decided cards
 * and decision comments). One pattern for the web's chips (`apps/web/src/components/markdown/refs.ts` re-exports it),
 * the server's ref extraction and ⌘K.
 */

/**
 * A ref anywhere in prose: not part of a path, URL, query, identifier or longer ref (`minga/T-9`, `?q=T-9`, `#T-9`,
 * `xT-9`). A key ref (`DIC-12`) is 2–5 upper-case letters and digits; resolvers keep only refs that exist, so "GPT-5"
 * stays text. Global: use with `matchAll` / `replace`, never `test`/`exec` without resetting `lastIndex`.
 */
export const REF_PATTERN = /(?<![\w/.#=?&:-])(?:[GKITPRCWVOMD]|[A-Z][A-Z0-9]{1,4})-[1-9]\d{0,5}(?![\w-])/g

/** The distinct refs in a text, in first-appearance order. */
export const refsIn = (text: string): ReadonlyArray<string> => {
  const out: string[] = []
  if (!/[A-Z0-9]-[1-9]/.test(text)) return out
  for (const m of text.matchAll(REF_PATTERN)) if (!out.includes(m[0])) out.push(m[0])
  return out
}

export type RefPiece = string | { readonly ref: string }

/** Split prose into text and refs. Text that has none comes back as one piece (the same string). */
export const splitRefs = (text: string): ReadonlyArray<RefPiece> => {
  if (!/[A-Z0-9]-[1-9]/.test(text)) return [text]
  const out: RefPiece[] = []
  let last = 0
  for (const m of text.matchAll(REF_PATTERN)) {
    if (m.index > last) out.push(text.slice(last, m.index))
    out.push({ ref: m[0] })
    last = m.index + m[0].length
  }
  if (out.length === 0) return [text]
  if (last < text.length) out.push(text.slice(last))
  return out
}
